import type { Channel } from '../types';

const LAST_CHANNEL_KEY_PREFIX = 'borgee:chat:lastChannel:';

/**
 * Which channel heads the fallback chain once nothing is remembered.
 *
 * `onboarding` is for a member arriving with no channel in the URL: a brand
 * new member should land in the welcome channel. `main` is for replacing a
 * channel the member just lost (left, deleted, archived away): they have
 * already been introduced, so the guild's general channel is the right
 * landing spot and sending them back to onboarding would read as a step
 * backwards.
 */
export type FallbackChannelPreference = 'onboarding' | 'main';

// localStorage throws instead of returning null under private browsing and
// when the origin is out of quota. The remembered channel is a convenience,
// so both accessors degrade to "nothing remembered" rather than propagating.
const loadLastChannelId = (guildId: string): string | null => {
  try {
    return window.localStorage.getItem(LAST_CHANNEL_KEY_PREFIX + guildId);
  } catch {
    return null;
  }
};

export const rememberLastChannelId = (guildId: string, channelId: string): void => {
  try {
    window.localStorage.setItem(LAST_CHANNEL_KEY_PREFIX + guildId, channelId);
  } catch {
    // Losing the memory only costs the member one extra click next time.
  }
};

const isWelcomeChannel = (channel: Channel): boolean =>
  channel.name === 'welcome' || channel.name?.startsWith('welcome-') === true;

/**
 * Picks the channel to land on whenever the route names none, or names one
 * the member can no longer reach.
 *
 * `channels` must be the guild's current list: a remembered id missing from it
 * is stale — the channel was deleted, left, or belongs to another guild — and
 * falls through to the preference chain rather than being treated as an error.
 * A `guildId` of `null` means no guild is active yet, so nothing can have been
 * remembered for it.
 */
export const resolveDefaultChannel = (
  channels: Channel[],
  guildId: string | null,
  preference: FallbackChannelPreference,
): Channel | undefined => {
  const textChannels = channels.filter(channel => channel.channel_type === 0);
  const rememberedId = guildId === null ? null : loadLastChannelId(guildId);
  const remembered = rememberedId === null ? undefined : textChannels.find(c => c.id === rememberedId);
  if (remembered) return remembered;

  const general = preference === 'main' ? textChannels.find(c => c.name === 'general') : undefined;
  return general ?? textChannels.find(isWelcomeChannel) ?? textChannels[0];
};
